<template>
  <m-pageframe title="{organization_label}: API Call Log">
    <v-container fluid>

      <!-- Header Information Section -->
      <v-card outlined class="mb-4">
        <v-card-text>
          <div class="text-h6 mb-2">About API Call Logs</div>
          <p class="text-body-2 mb-3">
            This page displays logs for external API calls made to your organization's RedChirp endpoints. You can
            search log content, filter by endpoint, and view detailed request/response data by expanding a row. Note
            that custom time range searches are limited to a 14 day maximum period.
          </p>
          <div class="text-subtitle-2">Advanced Searching</div>
          <p class="text-body-2 mb-0">
            Search is powered by AWS CloudWatch. You can enter in any text to search broadly across all fields. However,
            if you need more targeted queries, you can use the
            <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/logs/FilterAndPatternSyntax.html#matching-terms-regex"
              target="_blank" class="text-decoration-none">
              AWS filter pattern syntax <v-icon small>mdi-open-in-new</v-icon>
            </a>
            directly in the search box. Any fields you see in the full message details can be searched on. For example, to find all <code>GET</code> requests with a response code in the 400s:
            <br /><code>{ $.https_response_status_code >= 400 && $.https_response_status_code &lt; 500 && $.method = "GET" }</code>
          </p>
        </v-card-text>
      </v-card>
      <!--Spacer-->
      <v-row>
        <v-col>
          <v-divider />
        </v-col>
      </v-row>
      <!--Spacer-->
      <!--Spacer-->
      <v-row>
        <v-col>
        </v-col>
      </v-row>

      <v-row align="center">
        <v-col cols="12" md="4">
          <v-text-field v-model="full_text" label="Search" prepend-icon="mdi-magnify" dense autocomplete="off"
            :error-messages="search_error_message ? [search_error_message] : []" @input="onFullTextChanged" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="endpoint_selection" :items="endpoint_options" label="Endpoint" item-text="label"
            item-value="value" dense clearable @change="onEndpointChanged" />
        </v-col>
        <v-col cols="12" :md="time_range === 'custom' ? 2 : 5">
          <v-select v-model="time_range" :items="time_options" label="Time range" dense @change="onTimeRangeChanged" />
        </v-col>

        <v-col v-if="time_range === 'custom'" cols="12" md="3">
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu v-model="menu_start" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="custom_start_pretty" label="Start time" prepend-icon="mdi-calendar" readonly
                    dense v-bind="attrs" v-on="on" />
                </template>
                <v-card>
                  <v-card-text class="pa-0">
                    <v-row no-gutters>
                      <v-col cols="12" md="auto">
                        <v-date-picker v-model="start_date" :max="end_date || max_date" no-title scrollable />
                      </v-col>
                      <v-col cols="12" md="auto">
                        <v-time-picker v-model="start_time_str" ampm-in-title format="ampm" scrollable />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn text color="primary" @click="applyCustomTime('start')">Done</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="menu_end" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="custom_end_pretty" label="End time" prepend-icon="mdi-calendar" readonly dense
                    v-bind="attrs" v-on="on" />
                </template>
                <v-card>
                  <v-card-text class="pa-0">
                    <v-row no-gutters>
                      <v-col cols="12" md="auto">
                        <v-date-picker v-model="end_date" :min="start_date" :max="max_date" no-title scrollable />
                      </v-col>
                      <v-col cols="12" md="auto">
                        <v-time-picker v-model="end_time_str" :min="min_end_time" ampm-in-title format="ampm"
                          scrollable />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn text color="primary" @click="applyCustomTime('end')">Done</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- CloudWatch Query Display -->
      <v-row v-if="computed_cloudwatch_query">
        <v-col cols="12">
          <v-card outlined class="mb-3">
            <v-card-subtitle class="pb-2">
              CloudWatch Filter Query
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <code class="cloudwatch-query">{{ computed_cloudwatch_query }}</code>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-switch v-model="live_tail_running" @change="toggleLiveTail">
            <template v-slot:label>
              <div class="d-flex align-center">
                Live Tail
                <div v-if="live_tail_running" class="live-indicator ml-2"></div>
              </div>
            </template>
          </v-switch>
        </v-col>
      </v-row>

      <v-data-table :headers="headers" :items="results" :items-per-page="50" class="elevation-1" show-expand
        :expanded.sync="expanded_rows" item-key="_row_id" :loading="is_loading" loading-text="Loading... Please wait">
        <template v-slot:[`item.timestamp_ms`]="{ item }">
          {{ $rc.pretty(item.timestamp_ms, 'date_and_time') }}
        </template>

        <template v-slot:[`item.snippet`]="{ item }">
          <div class="snippet" v-html="item.message_text_highlighted"></div>
        </template>

        <template v-slot:expanded-item="{ headers: dtHeaders, item }">
          <td :colspan="dtHeaders.length">
            <div class="expanded">
              <div class="d-flex align-center">
                <div class="expanded-title mr-2">Full message</div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="copyToClipboard(prettyJson(item.message))">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ copy_tooltip_text }}</span>
                </v-tooltip>
              </div>
              <pre v-html="highlight(prettyJson(item.message), (full_text || '').toLowerCase())"></pre>
            </div>
          </td>
        </template>
      </v-data-table>

    </v-container>
  </m-pageframe>
</template>

<script>
const Time = require('@/js/time.js');

export default {
  data() {
    const now = Date.now();
    const to_iso = (ms) => new Date(ms).toISOString().slice(0, 10);
    const to_time_str = (ms) => {
      const d = new Date(ms);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    return {
      headers: [
        { text: 'Time', value: 'timestamp_ms', width: '200px' },
        { text: 'Method', value: 'message.method' },
        { text: 'Path', value: 'message.path' },
        { text: 'HTTP Code', value: 'message.https_response_status_code' },
        { text: 'Execution Time (ms)', value: 'message.execution_time_ms' },
        { text: 'Credential', value: 'message.credential_id' },
        { text: 'Message', value: 'snippet' },
      ],
      results: [],
      expanded_rows: [],
      full_text: null,
      search_error_message: null,
      endpoint_selection: null,
      endpoint_options: [],
      time_range: '12h',
      time_options: [
        { text: 'Last 5 minutes', value: '5m' },
        { text: 'Last 30 minutes', value: '30m' },
        { text: 'Last 1 hour', value: '1h' },
        { text: 'Last 12 hours', value: '12h' },
        { text: 'Last 24 hours', value: '24h' },
        { text: 'Last 48 hours', value: '48h' },
        { text: 'Custom…', value: 'custom' },
      ],
      debounce_timer: null,
      start_date: to_iso(now - 24 * 60 * 60 * 1000),
      end_date: to_iso(now),
      start_time_str: to_time_str(now - 24 * 60 * 60 * 1000),
      end_time_str: to_time_str(now),
      max_date: to_iso(now),
      menu_start: false,
      menu_end: false,
      is_loading: false,
      live_tail_running: false,
      live_tail_timer_id: null,
      copy_tooltip_text: 'Copy to clipboard',
    };
  },
  computed: {
    custom_start_pretty() {
      const date = new Date(this.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const time = Time.wallTimePrettyPrintUs(this.start_time_str);
      return `${date}, ${time}`;
    },
    custom_end_pretty() {
      const date = new Date(this.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const time = Time.wallTimePrettyPrintUs(this.end_time_str);
      return `${date}, ${time}`;
    },
    min_end_time() {
      return this.start_date === this.end_date ? this.start_time_str : null;
    },
    computed_cloudwatch_query() {
      const has_endpoint = this.endpoint_selection;
      const has_text = this.full_text && this.full_text.trim();

      if (!has_endpoint && !has_text) {
        return null;
      }

      // If only text search (no endpoint), use raw text
      if (!has_endpoint && has_text) {
        return this.full_text.trim();
      }

      // Build endpoint path with wildcards
      const cloudwatch_path = this.endpoint_selection
        .split('/')
        .map(segment => segment.startsWith(':') ? '*' : segment)
        .join('/');

      // If only endpoint (no text), just return path filter
      if (has_endpoint && !has_text) {
        return `{ $.path = "${cloudwatch_path}" }`;
      }

      // Both endpoint and text - build complex query
      const text_value = this.full_text.trim();
      return `{ $.path = "${cloudwatch_path}" && $.*= "*${text_value}*" }`;
    }
  },
  mounted() {
    this.loadEndpoints();
    this.doUpdate();
  },
  methods: {
    toggleLiveTail(is_running) {
      if (is_running) {
        this.expanded_rows = [];
        this.results = [];
        this.time_range = '5m';
        this.doUpdate();
        this.live_tail_timer_id = setInterval(this.doUpdate, 7000);
      } else {
        if (this.live_tail_timer_id) {
          clearInterval(this.live_tail_timer_id);
          this.live_tail_timer_id = null;
        }
        this.doUpdate();
      }
    },
    loadEndpoints() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/external/api-call-log/endpoints',
        params: { organization_id: this.$route.params.organization_id },
        on_success: (data) => {
          this.endpoint_options = (data.endpoints || []).map(p => ({ label: p, value: p }));
        },
        on_error: (error) => {  }
      });
    },
    onEndpointChanged() {
      this.doUpdate();
    },
    onFullTextChanged() {
      if (this.debounce_timer) clearTimeout(this.debounce_timer);
      this.debounce_timer = setTimeout(() => this.doUpdate(), 600);
    },
    onTimeRangeChanged() {
      if (this.live_tail_running && this.time_range !== 'custom') {
        this.live_tail_running = false;
        this.toggleLiveTail(false);
      }
      this.doUpdate();
    },
    applyCustomTime(which) {
      if (which === 'start') this.menu_start = false;
      if (which === 'end') this.menu_end = false;

      let start_ms = this.buildTimeFromCustom(this.start_date, this.start_time_str);
      let end_ms = this.buildTimeFromCustom(this.end_date, this.end_time_str);

      // Enforce order
      if (start_ms > end_ms) {
        if (which === 'start') {
          this.end_date = this.start_date;
          this.end_time_str = this.start_time_str;
          end_ms = start_ms;
        } else {
          this.start_date = this.end_date;
          this.start_time_str = this.end_time_str;
          start_ms = end_ms;
        }
      }

      // Enforce 14-day maximum
      const max_span_ms = 14 * 24 * 60 * 60 * 1000;
      if (end_ms - start_ms > max_span_ms) {
        if (which === 'end') {
          // If user changed end time, adjust start time
          start_ms = end_ms - max_span_ms;
          const start_date = new Date(start_ms);
          this.start_date = start_date.toISOString().slice(0, 10);
          this.start_time_str = `${String(start_date.getHours()).padStart(2, '0')}:${String(start_date.getMinutes()).padStart(2, '0')}`;
        } else {
          // If user changed start time, adjust end time
          end_ms = start_ms + max_span_ms;
          const end_date = new Date(end_ms);
          this.end_date = end_date.toISOString().slice(0, 10);
          this.end_time_str = `${String(end_date.getHours()).padStart(2, '0')}:${String(end_date.getMinutes()).padStart(2, '0')}`;
        }
      }

      this.doUpdate();
    },
    buildTimeFromCustom(date_iso, time_str) {
      const [hours, minutes] = (time_str || '00:00').split(':').map(Number);
      const date = new Date(`${date_iso}T00:00:00`);
      date.setHours(hours, minutes);
      return date.getTime();
    },
    calcTimeWindow() {
      if (this.live_tail_running) {
        const now = Date.now();
        return { start_time_ms: now - 5 * 60 * 1000, end_time_ms: now };
      }

      if (this.time_range === 'custom') {
        return {
          start_time_ms: this.buildTimeFromCustom(this.start_date, this.start_time_str),
          end_time_ms: this.buildTimeFromCustom(this.end_date, this.end_time_str)
        };
      }

      const now = Date.now();
      const map = {
        '5m': 5 * 60 * 1000,
        '30m': 30 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '12h': 12 * 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '48h': 48 * 60 * 60 * 1000,
      };
      const span = map[this.time_range] || map['12h'];
      return { end_time_ms: now, start_time_ms: now - span };
    },
    doUpdate() {
      const { start_time_ms, end_time_ms } = this.calcTimeWindow();
      this.is_loading = true;
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/external/api-call-log',
        params: {
          organization_id: this.$route.params.organization_id,
          full_text: this.computed_cloudwatch_query,
          start_time_ms,
          end_time_ms,
        },
        on_success: (data) => {
          this.search_error_message = null;
          const needle = (this.full_text || '').toLowerCase();
          const new_results = (data.results || []).map((r) => ({
            _row_id: `${r.timestamp_ms}`,
            ...r,
            message_text_highlighted: this.highlight(JSON.stringify(r.message || ''), needle),
          }));

          //If live tail is running we should only append any new results based on _row_id, new results should be on top based on timestamp
          if (this.live_tail_running) {
            const new_row_ids = new_results.map(r => r._row_id);
            const old_row_ids = this.results.map(r => r._row_id);
            const new_rows = new_results.filter(r => !old_row_ids.includes(r._row_id));
            this.results = new_rows.concat(this.results);
            this.expanded_rows = this.expanded_rows.filter(r => new_row_ids.includes(r._row_id));

          }
          else {
            this.results = new_results;
            this.expanded_rows = [];
          }

          this.is_loading = false;
        },
        on_error: (error) => {
          this.is_loading = false;
          this.search_error_message = (error && error.message) ? error.message : 'Unable to load API call log';
        },
      });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.copy_tooltip_text = 'Copied!';
        setTimeout(() => {
          this.copy_tooltip_text = 'Copy to clipboard';
        }, 2000);
      }).catch(err => {
        this.copy_tooltip_text = 'Failed to copy';
        setTimeout(() => {
          this.copy_tooltip_text = 'Copy to clipboard';
        }, 2000);
      });
    },
    prettyJson(obj) {
      try { return JSON.stringify(obj, null, 2); } catch (e) { return String(obj); }
    },
    escapeRegex(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    highlight(text, needle) {
      if (!needle) return text;
      try {
        const re = new RegExp(this.escapeRegex(needle), 'ig');
        return text.replace(re, (m) => `<mark>${m}</mark>`);
      } catch (e) { return text; }
    },
  },
  beforeDestroy() {
    if (this.live_tail_timer_id) { clearInterval(this.live_tail_timer_id); }
    if (this.debounce_timer) { clearTimeout(this.debounce_timer); }
  },
};
</script>

<style scoped>
pre {
  max-height: 60vh;
  overflow: auto;
  background: #111;
  color: #0f0;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

mark {
  background: #ffee58;
  padding: 0 2px;
  border-radius: 2px;
}

.snippet {
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expanded {
  background: #f7f7f7;
  padding: 8px 16px 16px 16px;
  border-radius: 4px;
}

.expanded-title {
  font-weight: 600;
  color: #333;
}

.live-indicator {
  width: 10px;
  height: 10px;
  background-color: #4CAF50;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }

  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.cloudwatch-query {
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #333;
  display: block;
  word-break: break-all;
}
</style>